import { createAsyncThunk } from "@reduxjs/toolkit"
import { notification } from "antd"

import { notificationOptions } from "../../../constants/notification.config.ts"
import i18n from "../../../i18n.ts"
import { errorSave } from "../../../redux/errors.slice"
import { rejectWithHandledError } from "../../../utils/handlers/reject-with-handled-error.ts"
import type { RootState } from "../../../store.ts"
import { unwrapEnvelope } from "../../../utils/api/unwrap-envelope.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import { mapDeletionErrorDetails } from "../../AccountDeletionRequestsPage/api/map-deletion-error.ts"
import { updateProfile } from "../../LoginPage/redux/auth.slice.ts"
import * as idConfigServices from "../api/id-configuration.services.ts"
import * as services from "../api/services"
import { type RequestAccountDeletionParams } from "../api/types.ts"
import { type IdConfiguration, type SettingUser } from "./settings.types.ts"

export const fetchUser = createAsyncThunk(
  "settings/fetchUser",
  async (
    params: string | { id: string; team?: string },
    { dispatch, rejectWithValue }
  ) => {
    const { id, team } =
      typeof params === "string" ? { id: params, team: undefined } : params
    const response = await services.getUser(id, team)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.fetchUser"
      })
    }

    return unwrapEnvelope<SettingUser>(response.data)
  }
)

export const updateUser = createAsyncThunk(
  "settings/updateUser",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.updateUser(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.updateUser"
      })
    }

    showResponseMessage(response, "User updated successfully.")
    // dispatch(fetchUserInfo())
    const user = response?.data?.details?.data

    // Update profile in Redux
    if (user) {
      dispatch(updateProfile({ user }))
    }

    return user
  }
)

export const updatePassword = createAsyncThunk(
  "settings/updatePassword",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.updatePassword(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.updatePassword"
      })
    }

    showResponseMessage(response, "Password updated successfully.")

    return response.data
  }
)

export const changeEmail = createAsyncThunk(
  "settings/changeEmail",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.requestChangeEmail(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.changeEmail"
      })
    }

    showResponseMessage(response, "User updated successfully")

    return response.data
  }
)

export const confirmEmail = createAsyncThunk(
  "settings/confirmEmail",
  async (params: any, { dispatch, rejectWithValue }) => {
    const response = await services.confirmChangeEmail(params)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.confirmEmail"
      })
    }

    showResponseMessage(response, "User updated successfully")

    return response.data
  }
)

export const requestAccountDeletion = createAsyncThunk<
  SettingUser | null,
  RequestAccountDeletionParams,
  { state: RootState }
>(
  "settings/requestAccountDeletion",
  async (params, { dispatch, rejectWithValue, getState }) => {
    const response = await services.requestAccountDeletion(params)

    if (!response.ok) {
      dispatch(errorSave(mapDeletionErrorDetails(response)))
      return rejectWithValue(response.data)
    }

    // Nothing is deleted yet — the endpoint files a request a GCC Admin still
    // has to approve.
    notification.success({
      ...notificationOptions.success,
      message: i18n.t("accountDeletion.toast.requested") as string,
      description: i18n.t("accountDeletion.toast.requestedDescription") as string
    })

    // `details.data` is the deletion request ({id, status, created_at}), not a
    // user — casting it into the profile would corrupt the store.
    const requestedAt =
      (response?.data?.details?.data?.created_at as string | undefined) ??
      new Date().toISOString()

    const current = getState().settings.user
    const user = current
      ? { ...current, account_deletion_requested_at: requestedAt }
      : null
    if (user) {
      dispatch(updateProfile({ user }))
    }

    return user
  }
)

export const fetchIdConfiguration = createAsyncThunk(
  "settings/fetchIdConfiguration",
  async (_: void, { rejectWithValue }) => {
    const response = await idConfigServices.getIdConfiguration()

    // A failed load is not worth a global error toast: the screen degrades to
    // its defaults. Only a failed save (below) surfaces to the user.
    if (!response.ok) {
      return rejectWithValue(response.data ?? null)
    }

    return unwrapEnvelope<IdConfiguration>(response.data)
  }
)

export const updateIdConfiguration = createAsyncThunk(
  "settings/updateIdConfiguration",
  async (
    payload: Partial<Pick<IdConfiguration, "mode" | "start_from">>,
    { dispatch, rejectWithValue }
  ) => {
    const response = await idConfigServices.updateIdConfiguration(payload)

    if (!response.ok) {
      return rejectWithHandledError(response, {
        dispatch,
        rejectWithValue,
        flow: "settings.updateIdConfiguration"
      })
    }

    showResponseMessage(response, "Configuration saved.")

    return unwrapEnvelope<IdConfiguration>(response.data)
  }
)
